import { Link } from "react-router-dom";

import {
  DASH,
  fmtInt,
  isPass,
  roleWords,
  shortDate,
  useMe,
  useSHDistricts,
  useSHGodowns,
  useSHInvitations,
  useSHReports,
  useSHState,
} from "./data";
import type { Invitation } from "../lib/api";
import { Tray } from "./Tray";

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

/** One activity row derived from an invitation the state head sent. */
function inviteTick(inv: Invitation) {
  if (inv.accepted_at) {
    return {
      date: shortDate(inv.accepted_at),
      title: `${inv.email} accepted the invite`,
      tag: "tag-live",
      label: "Invite accepted",
    };
  }
  if (inv.revoked_at) {
    return {
      date: shortDate(inv.revoked_at),
      title: `Invite to ${inv.email} revoked`,
      tag: "tag-off",
      label: "Revoked",
    };
  }
  return {
    date: shortDate(inv.created_at),
    title: `Invite sent to ${inv.email}`,
    tag: "tag-setup",
    label: "Invite sent",
  };
}

export function Overview() {
  const meQ = useMe();
  const stateQ = useSHState();
  const districtsQ = useSHDistricts();
  const godownsQ = useSHGodowns();
  const reportsQ = useSHReports();
  const invitesQ = useSHInvitations();

  const firstName = meQ.data?.user.first_name;
  const invites = invitesQ.data?.invitations ?? [];
  const state = stateQ.data?.state;
  const districts = districtsQ.data?.districts ?? [];
  const godowns = godownsQ.data?.godowns ?? [];
  const reports = reportsQ.data?.reports ?? [];

  const passed = reports.filter((r) => isPass(r.verdict)).length;
  const passRate = reports.length
    ? Math.round((passed / reports.length) * 100)
    : null;

  // Reports per district, reached through each district's godowns.
  const byDistrict = districts.map((d) => {
    const gids = new Set(
      godowns.filter((g) => g.district_id === d.id).map((g) => g.id),
    );
    const rs = reports.filter((r) => r.godown_id != null && gids.has(r.godown_id));
    return {
      district: d,
      passed: rs.filter((r) => isPass(r.verdict)).length,
      total: rs.length,
    };
  });

  return (
    <>
      <p className="crumb">
        {(state?.name ?? "STATE").toUpperCase()} / STATE HEAD
      </p>
      <div className="page-head">
        <div>
          <h1>
            {greeting()}
            {firstName ? `, ${firstName}` : ""}
          </h1>
          <p>
            Grain grading and reports across your districts, updated as field
            devices file them.
          </p>
        </div>
        <div className="page-actions">
          <Link className="btn btn-gold" to="/sh/districts">
            Add district
          </Link>
        </div>
      </div>

      <div className="stats">
        <div className="stat">
          <span>DISTRICTS</span>
          <b>{districtsQ.isLoading ? DASH : fmtInt(districts.length)}</b>
          <i>in {state?.name ?? "your state"}</i>
        </div>
        <div className="stat">
          <span>GODOWNS</span>
          <b>{godownsQ.isLoading ? DASH : fmtInt(godowns.length)}</b>
          <i>across your districts</i>
        </div>
        <div className="stat">
          <span>REPORTS</span>
          <b>{reportsQ.isLoading ? DASH : fmtInt(reports.length)}</b>
          {passRate != null ? (
            <i className="up">
              {fmtInt(passed)} passed · {passRate}%
            </i>
          ) : (
            <i>{DASH}</i>
          )}
        </div>
        <div className="stat">
          <span>MEDIAN TURNAROUND</span>
          <b>{DASH}</b>
          <i>not tracked yet</i>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <h2>Reports by district</h2>
            <p>Tray fill shows reports passed out of reports filed.</p>
          </div>
          <div className="right">
            <Link className="btn" to="/sh/districts">
              Open districts
            </Link>
          </div>
        </div>
        <div className="card-body">
          {districtsQ.isLoading && <div className="tray-cap">Loading…</div>}
          {!districtsQ.isLoading && byDistrict.length === 0 && (
            <div className="empty" style={{ border: 0 }}>
              <b>No districts yet</b>
              <p>
                Add your first district and invite its head to start seeing
                reports here.
              </p>
              <Link className="btn btn-gold" to="/sh/districts">
                Add district
              </Link>
            </div>
          )}
          {byDistrict.map((row) => (
            <div className="tk-row" key={row.district.id}>
              <span className="dname">
                {row.district.name}
                <em>
                  {row.district.code || `DISTRICT #${row.district.id}`}
                  {row.district.zone
                    ? ` · ${row.district.zone.toUpperCase()}`
                    : ""}
                </em>
              </span>
              <span>
                <Tray passed={row.passed} total={row.total} />
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <h2>Recent activity</h2>
            <p>Invitations you've sent to district heads.</p>
          </div>
        </div>
        {invites.length === 0 ? (
          <div className="card-body">
            <div className="empty" style={{ border: 0 }}>
              <b>Nothing yet</b>
              <p>
                Invitations you send to district heads will show up here as
                they're sent and accepted.
              </p>
            </div>
          </div>
        ) : (
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {invites.map((inv) => {
              const t = inviteTick(inv);
              return (
                <div className="tick" key={inv.id}>
                  <span className="tick-id">{t.date}</span>
                  <div>
                    <b>{t.title}</b>
                    <p>
                      {roleWords(inv.role)} · {inv.target_scope_type} #
                      {inv.target_scope_id}
                    </p>
                  </div>
                  <span className={"tag " + t.tag}>
                    <span className="dot" />
                    {t.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
